import * as React from "react";
import {
    Link,
    makeStyles,
    Text
} from "@fluentui/react-components";
import { CodeBlock } from "../../components/CodeBlock";
import { CardSection } from "../../components/CardSection";
import { useNavigate } from "react-router-dom";

const useStyles = makeStyles({
    container: {
        display: "flex",
        flexDirection: "column",
        gap: "2rem",
        padding: "2rem",
        width: "80%",
        margin: "0 auto",
        maxWidth: "1400px",
    },
    ul: {
        paddingLeft: "1.25rem",
        marginTop: "0.5rem",
        marginBottom: "0.5rem",
    },
});

export const Id = "9a49af57-388b-4ed2-ab9e-a942341b9a6e";
export const Title = "How to use Ianus Guard inside Plugins?";
export const Subtitle = "Explanation on how to use Ianus Guard inside Plugins";
export const Description = "Learn how to integrate Ianus Guard into both legacy and modern Dataverse plugins";
export const Keywords = ["developer", "c#", "procode", "plugin"];

const codeSampleLegacy = `var publisherId = new Guid("41fd96d4-26eb-44fc-a8a3-4ce890fd955c");
var productId = new Guid("4219c0e1-1b29-4b94-abb3-f7d9ac5617c7");
var publicKeys = new string[]
{
    @"-----BEGIN PUBLIC KEY-----
    MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAznOMDcbaUIRERL772Hip
    3/znf6MEkd7R2bEhyFzRbTZ0+KxBo20uZLlPv/zQRbaLg8VVMdbaY8FX4+hNBmnw
    hnyX6V1CwqEXp9DEHHOYuddcv20DhmcB6yyXh8KMUBVmUd7kYwE3goYyMzjMiNYr
    QcLoFUkx4IT6PqT1EcaKbBrQTQjhxgq3wyt/ZKW73aM2VuA9oLnyWW+5K031Evsa
    gRQxeyL43JwDJ8SGmEl+fuoh0htiI4sZNmdeyYk5zM7BdKWYA61j7a66I219ajbP
    E/+hAWsY4/jco60G+M0+x9Dd5gl75utQ0Qna9a+UxNHEHpqbiqpKCVtBhxvWoKVa
    1QIDAQAB
    -----END PUBLIC KEY-----"
};

var request = new IanusValidationRequest
{
    PublisherId = publisherId,
    ProductId = productId,
    PublicKeys = publicKeys
};

var result = LicenseValidator.ValidateLicense(request, publisherService);

// Properties of 'result': result.IsValid (bool), result.Reason (string), result.License (claims or null)
`;

const codeSamplePluginPackage = `var publisherId = new Guid("41fd96d4-26eb-44fc-a8a3-4ce890fd955c");
var productId = new Guid("4219c0e1-1b29-4b94-abb3-f7d9ac5617c7");
var publicKeys = new string[]
{
    @"-----BEGIN PUBLIC KEY-----
    MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAznOMDcbaUIRERL772Hip
    3/znf6MEkd7R2bEhyFzRbTZ0+KxBo20uZLlPv/zQRbaLg8VVMdbaY8FX4+hNBmnw
    hnyX6V1CwqEXp9DEHHOYuddcv20DhmcB6yyXh8KMUBVmUd7kYwE3goYyMzjMiNYr
    QcLoFUkx4IT6PqT1EcaKbBrQTQjhxgq3wyt/ZKW73aM2VuA9oLnyWW+5K031Evsa
    gRQxeyL43JwDJ8SGmEl+fuoh0htiI4sZNmdeyYk5zM7BdKWYA61j7a66I219ajbP
    E/+hAWsY4/jco60G+M0+x9Dd5gl75utQ0Qna9a+UxNHEHpqbiqpKCVtBhxvWoKVa
    1QIDAQAB
    -----END PUBLIC KEY-----"
};

var result = LicenseValidation.VerifyLicense(publisherId, productId, publicKeys, localPluginContext.InitiatingUserService);

// Properties of 'result': result.IsValid (bool), result.Reason (string), result.License (claims or null)
`;

export const Component: React.FC = React.memo(() => {
    const styles = useStyles();
    const navigate = useNavigate();

    return (
        <div className={styles.container}>
            <CardSection title="Scenarios">
                <Text>
                    Ianus supports two plugin models in Dataverse:
                    <br />
                    <strong>Legacy Plugins</strong> (.dll-based) and <strong>Plugin Packages</strong> (.nupkg-based).
                    <br />
                    Legacy plugins require embedded source code; plugin packages support NuGet packages.
                    Be sure to have imported our Ianus Guard solution, preferably via <Link href="https://marketplace.microsoft.com/en-us/product/saas/ianuasoftware.ianus_guard?tab=overview">Microsoft AppSource</Link> or alternatively from <Link href="https://github.com/Ianua-Software/Ianus-Clients/blob/main/solutions/IanusGuard_managed.zip" target="_blank">
                        GitHub
                    </Link> into your organization before starting.
                </Text>
            </CardSection>

            <CardSection title="Legacy Plugins">
                <Text>
                    Install our source-based NuGet package to embed validation logic:
                </Text>
                <CodeBlock code="dotnet add package Ianua.Ianus.Dataverse.Client.Light" language="bash" />
                <Text>Or use Package Manager:</Text>
                <CodeBlock code="NuGet\\Install-Package Ianua.Ianus.Dataverse.Client.Light" language="bash" />
                <Text>Sample usage:</Text>
                <CodeBlock code={codeSampleLegacy} language="csharp" />
                <Text>
                    Ensure your <code>PublisherId</code>, <code>ProductId</code>, and <code>PublicKeys</code> match what’s shown in your Ianus Guard portal.
                    If <code>result.IsValid</code> is <code>false</code>, block execution and log <code>result.Reason</code>.
                    <br />
                    For performance, cache and re-check periodically (e.g. every 5–60 minutes).
                </Text>
            </CardSection>

            <CardSection title="Plugin Packages">
                <Text>Install the full Ianus client with NuGet:</Text>
                <CodeBlock code="dotnet add package Ianua.Ianus.Dataverse.Client" language="bash" />
                <Text>Or:</Text>
                <CodeBlock code="NuGet\\Install-Package Ianua.Ianus.Dataverse.Client" language="bash" />
                <Text>This includes <strong>BouncyCastle</strong> for cryptographic validation.</Text>
                <Text>Sample usage:</Text>
                <CodeBlock code={codeSamplePluginPackage} language="csharp" />
                <Text>
                    Validation behavior is identical to legacy plugins. Don’t forget to reject execution when invalid, and log the reason.
                    <br />
                    <br />
                    🛠 Want to explore the client code?{" "}
                    <Link href="https://github.com/Ianua-Software/Ianus-Clients" target="_blank">
                        View it on GitHub
                    </Link>
                </Text>
            </CardSection>

            <CardSection title="Information to replace in the above examples">
                <Text size={400}>
                    As a rule of thumb, all information that you need to enter has a "Copy Button" next to it.
                    <br/>
                    Where to find the information for replacing:
                </Text>
                <ul className={styles.ul}>
                    <li><b>publisherId</b>: ExternalId found on your publisher form</li>
                    <li><b>productId</b>: ExternalId found on your product form</li>
                    <li><b>publicKeys</b>: Found on your product form as "Public Key" inside "Product Versions"</li>
                </ul>
                <Text size={400}>
                    We advice you to start with dual signing early on, meaning that you should embed the public key from Ianus Guard portal, as well as a public key that you generate yourself.
                    That way you can always leave the platform if you ever feel the need to do so, without disturbing your operations (but please reach out first, maybe we can help).
                    A detailled instruction on how to do so can be found on <Link href="https://github.com/Ianua-Software/Ianus-Clients/tree/main/standalone" target="_blank">GitHub</Link>.
                </Text>
                <Text size={400}>
                    Don’t have an Ianus account yet? Follow the{" "}
                    <Link as="button" onClick={() => navigate("/docs/publisher/article/5f27bdcf-1055-4c66-8a39-eee72ec0a397")}>
                        Publisher getting started guide
                    </Link>.
                </Text>
            </CardSection>
        </div>
    );
});
